import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  Link as LinkIcon,
  MessageSquare,
  RefreshCcw,
  Pause,
  Play,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  useBudgetSubscription,
  useCreateSubscription,
  useManageSubscription,
} from "@/hooks/useSubscriptions";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v);

interface Props {
  budgetId: string;
  amount: number;
  leadPhone?: string | null;
  leadName?: string | null;
}

const statusLabel: Record<string, string> = {
  pending: "Aguardando autorização",
  authorized: "Ativa",
  paused: "Pausada",
  cancelled: "Cancelada",
};
const statusClass: Record<string, string> = {
  pending: "bg-warn-soft text-warn",
  authorized: "bg-ok-soft text-ok",
  paused: "bg-muted text-muted-foreground",
  cancelled: "bg-bad-soft text-bad",
};

/** "How the client pays the mensalidade" — sits next to BudgetPaymentPlan in the Pagamentos section. */
export function BudgetSubscriptionCard({ budgetId, amount, leadPhone, leadName }: Props) {
  const { data: sub, isLoading } = useBudgetSubscription(budgetId);
  const create = useCreateSubscription();
  const manage = useManageSubscription();
  const [confirmAction, setConfirmAction] = useState<"pause" | "resume" | "cancel" | null>(null);

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );

  const copyLink = () => {
    if (!sub?.init_point) return;
    navigator.clipboard.writeText(sub.init_point);
    toast.success("Link de autorização copiado");
  };

  const shareWhatsApp = () => {
    if (!leadPhone || !sub?.init_point) return;
    const phone = leadPhone.replace(/\D/g, "");
    const first = (leadName ?? "").split(" ")[0];
    const text = [
      `Olá${first ? ` ${first}` : ""}! Segue o link para autorizar a mensalidade de *${fmt(amount)}/mês*:`,
      sub.init_point,
      "",
      `Use o e-mail ${sub.payer_email ?? "cadastrado na proposta"} pra autorizar — o Mercado Pago cobra automaticamente todo mês depois disso.`,
    ].join("\n");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, "_blank");
  };

  const actionCopy: Record<
    "pause" | "resume" | "cancel",
    { title: string; description: string; confirmLabel: string }
  > = {
    pause: {
      title: "Pausar a cobrança mensal?",
      description: "O Mercado Pago para de cobrar até você retomar. Nenhum dado é perdido.",
      confirmLabel: "Pausar",
    },
    resume: {
      title: "Retomar a cobrança mensal?",
      description: "A cobrança volta a acontecer todo mês a partir de agora.",
      confirmLabel: "Retomar",
    },
    cancel: {
      title: "Cancelar a cobrança mensal?",
      description:
        "Encerra de vez essa assinatura no Mercado Pago. Pra cobrar de novo, é preciso ativar uma nova.",
      confirmLabel: "Cancelar cobrança",
    },
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-base">Mensalidade — {fmt(amount)}/mês</CardTitle>
            {sub && <Badge className={statusClass[sub.status]}>{statusLabel[sub.status]}</Badge>}
          </div>
          {!sub && (
            <p className="text-xs text-muted-foreground">
              Ativa um débito automático no Mercado Pago: o cliente cadastra o cartão uma vez e a
              cobrança repete todo mês até você cancelar.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {!sub ? (
            <Button onClick={() => create.mutate(budgetId)} disabled={create.isPending}>
              {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ativar cobrança mensal automática
            </Button>
          ) : sub.status === "cancelled" ? (
            <>
              <p className="text-sm text-muted-foreground">
                Cancelada
                {sub.canceled_at
                  ? ` em ${format(new Date(sub.canceled_at), "dd/MM/yyyy", { locale: ptBR })}`
                  : ""}
                .
              </p>
              <Button onClick={() => create.mutate(budgetId)} disabled={create.isPending}>
                {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Ativar novamente
              </Button>
            </>
          ) : (
            <>
              {sub.status === "pending" && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Cliente precisa autorizar com o e-mail <strong>{sub.payer_email}</strong> —
                    mande o link abaixo.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={copyLink}>
                      <LinkIcon className="mr-1.5 h-3.5 w-3.5" /> Copiar link
                    </Button>
                    {leadPhone && (
                      <Button size="sm" variant="outline" onClick={shareWhatsApp}>
                        <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {sub.status === "authorized" && sub.next_payment_date && (
                <p className="text-sm text-muted-foreground">
                  Próxima cobrança em{" "}
                  {format(new Date(sub.next_payment_date), "dd/MM/yyyy", { locale: ptBR })}.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {sub.status === "paused" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmAction("resume")}
                    disabled={manage.isPending}
                  >
                    <Play className="mr-1.5 h-3.5 w-3.5" /> Retomar
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmAction("pause")}
                    disabled={manage.isPending}
                  >
                    <Pause className="mr-1.5 h-3.5 w-3.5" /> Pausar
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="text-bad hover:text-bad"
                  onClick={() => setConfirmAction("cancel")}
                  disabled={manage.isPending}
                >
                  <XCircle className="mr-1.5 h-3.5 w-3.5" /> Cancelar
                </Button>
                {sub.status === "pending" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => create.mutate(budgetId)}
                    disabled={create.isPending}
                  >
                    <RefreshCcw className="mr-1.5 h-3.5 w-3.5" /> Gerar novo link
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!confirmAction}
        onOpenChange={(o) => {
          if (!o) setConfirmAction(null);
        }}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              {confirmAction && actionCopy[confirmAction].title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              {confirmAction && actionCopy[confirmAction].description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Voltar
            </AlertDialogCancel>
            <AlertDialogAction
              className={`h-[38px] rounded-[10px] text-[12.5px] font-semibold ${confirmAction === "cancel" ? "bg-bad text-bad-foreground hover:bg-bad/90" : ""}`}
              onClick={() => {
                if (confirmAction && sub)
                  manage.mutate({
                    subscription_id: sub.id,
                    budget_id: budgetId,
                    action: confirmAction,
                  });
                setConfirmAction(null);
              }}
            >
              {confirmAction && actionCopy[confirmAction].confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
